import {
  ORG_MEMBERSHIP_ROLES,
  ORG_ROLE_LABELS,
  type OrgMembershipRole,
} from "@/auth/membershipRoles";

export const ROLE_DESCRIPTIONS: Record<OrgMembershipRole, string> = {
  caregiver: "Versorgung und Pflegedokumentation",
  medicus: "Medikus-Bereich, Diagnosen, Behandlungen",
  board: "Mitglieder, Einladungen, offizielle Beiträge",
  organizer: "Veranstaltungen und Kalender",
  admin: "Mitglieder, Rollen, Einladungen, Vereinsdaten",
};

const MIN_ROLE = "Jedes Mitglied braucht mindestens eine Rolle.";
const ADMIN_LOCK =
  "Nur die Administration darf die Rolle Administration vergeben oder entziehen.";
const SOLE_SELF =
  "Du bist die einzige Person mit Administration. Vergib die Rolle zuerst an ein anderes Mitglied.";
const SOLE_OTHER =
  "Es muss mindestens eine aktive Administration im Verein bleiben.";
const READ_ONLY = "Du kannst die Rollen hier nur ansehen.";
const ADMIN_STATUS =
  "Nur die Administration darf ein Mitglied mit Administration deaktivieren oder reaktivieren.";
const STATUS_READ_ONLY = "Du kannst die Mitgliedschaft hier nur ansehen.";

export function formatRoleDiff(
  current: readonly string[],
  next: readonly string[],
): string {
  const added = ORG_MEMBERSHIP_ROLES.filter(
    (role) => next.includes(role) && !current.includes(role),
  );
  const removed = ORG_MEMBERSHIP_ROLES.filter(
    (role) => current.includes(role) && !next.includes(role),
  );
  const parts = [
    ...added.map((role) => `+ ${ORG_ROLE_LABELS[role]}`),
    ...removed.map((role) => `− ${ORG_ROLE_LABELS[role]}`),
  ];
  if (parts.length === 0) return "";
  return `Geändert: ${parts.join(" / ")}`;
}

export function rolesChanged(
  current: readonly string[],
  next: readonly string[],
): boolean {
  return formatRoleDiff(current, next) !== "";
}

export function isSoleActiveAdmin(
  members: readonly { profileId: string; status: string; roles: readonly string[] }[],
  profileId: string,
): boolean {
  const admins = members.filter(
    (member) => member.status === "active" && member.roles.includes("admin"),
  );
  return admins.length === 1 && admins[0]?.profileId === profileId;
}

export function roleCheckboxState(input: {
  role: OrgMembershipRole;
  selected: readonly string[];
  canChangeAdminRole: boolean;
  readOnly: boolean;
  soleActiveAdmin: boolean;
  isSelf: boolean;
}): { checked: boolean; disabled: boolean; hint: string | null } {
  const checked = input.selected.includes(input.role);
  if (input.readOnly) {
    return { checked, disabled: true, hint: READ_ONLY };
  }
  if (input.role === "admin" && !input.canChangeAdminRole) {
    return { checked, disabled: true, hint: ADMIN_LOCK };
  }
  if (input.role === "admin" && checked && input.soleActiveAdmin) {
    return {
      checked,
      disabled: true,
      hint: input.isSelf ? SOLE_SELF : SOLE_OTHER,
    };
  }
  if (checked && input.selected.length <= 1) {
    return { checked, disabled: true, hint: MIN_ROLE };
  }
  return { checked, disabled: false, hint: null };
}

export function toggleRole(
  selected: readonly OrgMembershipRole[],
  role: OrgMembershipRole,
): OrgMembershipRole[] {
  if (selected.includes(role)) {
    return selected.filter((item) => item !== role);
  }
  return ORG_MEMBERSHIP_ROLES.filter(
    (item) => selected.includes(item) || item === role,
  );
}

export function needsOwnAdminRemovalConfirm(input: {
  isSelf: boolean;
  currentRoles: readonly string[];
  nextRoles: readonly string[];
}): boolean {
  return (
    input.isSelf &&
    input.currentRoles.includes("admin") &&
    !input.nextRoles.includes("admin")
  );
}

/** Vorstand bleibt eine Leitungsrolle für die Mitgliederverwaltung. */
export function ownAdminRemovalMessage(nextRoles: readonly string[]): string {
  if (nextRoles.includes("board")) {
    return "Die Rolle Administration entfällt. Die Mitgliederverwaltung bleibt über die Rolle Vorstand erreichbar.";
  }
  return "Du verlierst danach den Zugriff auf die Mitgliederverwaltung.";
}

export type MembershipAction = {
  mode: "hidden" | "deactivate" | "reactivate";
  enabled: boolean;
  reason: string | null;
};

export function membershipAction(input: {
  isSelf: boolean;
  status: string;
  targetHasAdmin: boolean;
  canWrite: boolean;
  canChangeAdminMemberStatus: boolean;
}): MembershipAction {
  if (input.isSelf || input.status === "invited") {
    return { mode: "hidden", enabled: false, reason: null };
  }
  const mode = input.status === "disabled" ? "reactivate" : "deactivate";
  if (!input.canWrite) {
    return { mode, enabled: false, reason: STATUS_READ_ONLY };
  }
  if (input.targetHasAdmin && !input.canChangeAdminMemberStatus) {
    return { mode, enabled: false, reason: ADMIN_STATUS };
  }
  return { mode, enabled: true, reason: null };
}
